// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";

import { lastCurrentUserErrorAtom } from "../hooks/useCurrentUser";
import { showDedupedErrorToast } from "./dedupedToast";

/**
 * Subscribes to `lastCurrentUserErrorAtom` and shows a single Ant Design
 * toast per distinct error.
 *
 * Why this exists: `fetchCurrentUser` never throws (see its JSDoc), so the
 * global `QueryErrorHandler` never sees current-user failures. We still want
 * to notify the user about transient backend errors (429, 5xx, network), but
 * only ONCE per error — with ~11 `useCurrentUser` consumers × React
 * StrictMode, dispatching from inside `fetchCurrentUser` would still produce
 * a toast storm if routed through `message.error` directly.
 *
 * A `useRef` tracks a content key (`${status}:${message}`) of the
 * last-shown error. Object-identity comparison would re-fire on every
 * fresh `setLastError({...})` call even when the underlying failure is
 * identical. 401/403 are suppressed because they are expected on the
 * login page. The actual `message.error` call is routed through
 * `showDedupedErrorToast` so a global 429 that also trips `folderAtom`
 * and `resourceSlotListAtom` still only produces one visible toast.
 */
export default function CurrentUserErrorToaster() {
  const lastError = useAtomValue(lastCurrentUserErrorAtom);
  const lastShownKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastError == null) {
      lastShownKeyRef.current = null;
      return;
    }
    if (lastError.status === 401 || lastError.status === 403) {
      return;
    }
    const contentKey = `${lastError.status ?? "unknown"}:${lastError.message}`;
    if (lastShownKeyRef.current === contentKey) {
      return;
    }
    lastShownKeyRef.current = contentKey;
    showDedupedErrorToast(
      `currentUser:${lastError.status ?? "unknown"}`,
      lastError.message
    );
  }, [lastError]);

  return null;
}
